import React, { useState } from "react";
import {
  Box,
  Grid,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Link,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,

  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import { api } from "../api/api";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../redux/reducers/userReducer";
import { updateReferrerId } from "../redux/reducers/Reducer";
import GradientButton from "../components/common/GradiantButton";
import { logoutUser } from "../redux/reducers/userReducer";

export default function LoginPage() {
  const [loading, setLoading] = useState(false);
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const ReffererId = useSelector((state) => state.referer.ReffererId);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleLogin = async () => {
    setLoading(true);
    if (!userName || !password) {
      setSnackbar({
        open: true,
        message: "Please fill in both fields.",
        severity: "warning",
      });
      return;
    }

    try {
      const response = await api.login({ userName, password });
      if (response.status === 200) {
        dispatch(loginUser({ user: response.data }));
        localStorage.setItem("token", response.data.token);
        setSnackbar({
          open: true,
          message: "Login successful!",
          severity: "success",
        });

        setTimeout(() => {
          if (location.state?.from) {
            dispatch(updateReferrerId({ ReffererId }));
            navigate(location.state.from.pathname + location.state.from.search, {
              replace: true,
            });
          } else {
            navigate("/", { replace: true });
          }
        }, 1500);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Login failed!",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #e3f2fd52 0%, #f3e5f536 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Grid
          container
          sx={{
            height: "80vh",
            minHeight: "600px",
            maxHeight: "800px",
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)", // Lighter shadow
            border: "1px solid #e2e8f0", // Subtle border
          }}
        >
          {/* Left Side - Image Section (45% width) */}
          <Grid
            item
            xs={12}
            md={5.4}
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              justifyContent: "center",
              background: `
      linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.5)),
      url("https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1973&q=80")
    `,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              position: "relative",
              overflow: "hidden",
            }}
          >


            {/* Gradient Overlay */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "linear-gradient(135deg, rgba(98, 97, 97, 0.8) 0%, rgba(71, 70, 70, 0.8) 100%)",
                zIndex: 1,
              }}
            />

            {/* Overlay Content */}
            <Box
              sx={{
                textAlign: "center",
                color: "white",
                zIndex: 2,
                padding: 6,
                position: "relative",
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  color: "rgba(255, 255, 255, 1)",
                  fontWeight: "bold",
                  mb: 3,
                  textShadow: "2px 2px 8px rgba(0,0,0,0.5)",
                  fontSize: { md: "2.5rem", lg: "3rem" },
                  lineHeight: 1.2
                }}
              >
                Your Dream Property<br />Awaits at Jemins
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  color: "rgba(255, 255, 255, 1)",
                  opacity: 0.95,
                  textShadow: "1px 1px 4px rgba(0,0,0,0.5)",
                  maxWidth: "420px",
                  lineHeight: 1.6,
                  fontWeight: 250,
                  textAlign: "start"
                }}
              >
                Experience premium real estate with verified listings and exclusive properties.
                Jemins Builders & Developers brings you trusted opportunities in prime locations
                for your perfect dream Property.
              </Typography>

            </Box>
          </Grid>

          {/* Right Side - Login Form (55% width) */}
          <Grid
            item
            xs={12}
            md={6.6}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#ffffff",
            }}
          >
            <Box
              sx={{
                padding: { xs: 4, sm: 6, md: 8 },
                width: "100%",
                maxWidth: "500px",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 24,
                  right: 24,
                  zIndex: 10,
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    dispatch(logoutUser());
                    navigate("/");
                  }}
                  sx={{
                    borderRadius: "30px",
                    px: 3,
                    py: 1,
                    fontWeight: "bold",
                    textTransform: "none",
                    boxShadow: "0 8px 25px rgba(0,0,0,0.25)",
                  }}
                >
                  ← Go to Home
                </Button>
              </Box>




              {/* Header */}
              <Box sx={{ textAlign: "center", mb: 5 }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: "bold",
                    color: "#1e293b", // Dark gray color
                    mb: 2,
                    fontSize: { xs: "2rem", sm: "2.5rem" },
                  }}
                >
                  Login
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#64748b", // Medium gray
                    fontSize: "1.1rem",
                    fontWeight: 400,
                  }}
                >
                  Sign in to your account
                </Typography>
              </Box>

              {/* Form */}
              <Box
                component="form"
                noValidate
                autoComplete="off"
                sx={{ mt: 4 }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleLogin();
                  }
                }}
              >
                <TextField
                  fullWidth
                  required
                  label="Email or Mobile Number"
                  variant="outlined"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  sx={{
                    mb: 3,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover fieldset": {
                        borderColor: "#667eea",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#667eea",
                        borderWidth: 2,
                      },
                    },
                  }}
                />
                <TextField
                  fullWidth
                  required
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  sx={{
                    mb: 3,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover fieldset": {
                        borderColor: "#667eea",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#667eea",
                        borderWidth: 2,
                      },
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                          aria-label="toggle password visibility"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />


                {/* Forgot Password Link */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    mb: 4,
                  }}
                >
                  <Link
                    component={RouterLink}
                    to="/ForgetPassword"
                    state={{ from: location.state?.from }}
                    underline="hover"
                    sx={{
                      color: "#667eea",
                      fontWeight: 600,
                      fontSize: "0.95rem",
                      "&:hover": {
                        color: "#764ba2",
                      },
                    }}
                  >
                    Forgot Password?
                  </Link>
                </Box>

                {/* Login Button */}
                <GradientButton
                  width="100%"
                  text={loading ? "Signing In..." : "Sign In"}
                  onClick={handleLogin}
                  sx={{
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: "bold",
                    borderRadius: 2,
                    boxShadow: "0 8px 20px rgba(102, 126, 234, 0.3)",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 12px 25px rgba(102, 126, 234, 0.4)",
                    },
                    transition: "all 0.3s ease",
                    mb: 3,
                  }}
                />

                {/* Register Link */}
                <Typography
                  variant="body2"
                  align="center"
                  sx={{
                    mt: 4,
                    color: "#64748b",
                    fontSize: "1rem",
                  }}
                >
                  Don't have an account?{" "}
                  <Link
                    component={RouterLink}
                    to="/Register"
                    state={{ from: location.state?.from }}
                    underline="hover"
                    sx={{
                      color: "#667eea",
                      fontWeight: 600,
                      fontSize: "1rem",
                      "&:hover": {
                        color: "#764ba2",
                      },
                    }}
                  >
                    Create Account
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}

      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            borderRadius: 2,
            fontWeight: 500,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}